import { Box, Button, Tooltip, Container } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import CommonTable from 'components/common/CommonTable';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axios } from 'utils/axios';
import { CloudDownload, PictureAsPdf, Description } from '@mui/icons-material';
import useSettings from 'hooks/useSettings';

const TransactionReports = () => {
  const navigate = useNavigate();
  const [contactSites, setContactSites] = useState<any[]>([]);
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const columns = [
    { field: 'Id', headerName: 'Report Id', flex: 1, minWidth: 200 },
    { field: 'Status', headerName: 'Report Status', flex: 1, minWidth: 200 },
    {
      field: 'FileType',
      headerName: 'File Type',
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params === 'pdf' ? 'PDF File' : 'Excel File'}>
            {params === 'pdf' ? (
              <PictureAsPdf style={{ color: 'red' }} />
            ) : (
              <Description style={{ color: 'green' }} />
            )}
          </Tooltip>
        );
      }
    },
    {
      field: 'CreatedAt',
      headerName: 'Created At',
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        const CreatedAt = params; // Assuming params.value contains the date string

        if (!CreatedAt) return <span>N/A</span>;

        const date = new Date(CreatedAt);
        const formattedDate = new Intl.DateTimeFormat('en-GB', {
          weekday: 'long', // 'Monday'
          year: 'numeric', // '2025'
          month: 'long', // 'February'
          day: 'numeric', // '4'
          hour: 'numeric', // '2'
          minute: 'numeric', // '30'
          second: 'numeric', // '45'
          hour12: true // true for 12-hour clock, false for 24-hour
        }).format(date);

        return <span>{formattedDate}</span>;
      }
    },
    {
      field: 'downloadUrl',
      headerName: 'Download Report',
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        // Directly check if the params value contains the URL
        const downloadUrl = params; // Since params is the download URL

        const isDisabled = !downloadUrl; // If no URL, it's disabled

        return (
          <Tooltip
            title={isDisabled ? 'Report is not ready for download yet' : 'Click to download report'}
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudDownload />}
                disabled={isDisabled}
                href={isDisabled ? undefined : downloadUrl}
                target={isDisabled ? undefined : '_blank'}
                rel={isDisabled ? undefined : 'noopener noreferrer'}
              >
                {isDisabled ? 'Download' : 'Download'}
              </Button>
            </span>
          </Tooltip>
        );
      }
    }
  ];

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/rest/api/transaction/files?page=${page}&pageSize=${pageSize}`
      );
      console.log('Response:', response);

      const data = response?.data?.requests || [];
      const totalRecords = response?.data?.pagination?.totalRecords || 0;

      setContactSites(data);
      setTotalRows(totalRecords);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setContactSites([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [page, pageSize]);

  return (
    <Container maxWidth={themeStretch ? false : 'lg'}>
      <HeaderBreadcrumbs
        heading="Reports"
        links={[
          { name: 'Dashboard', href: '/dashboard' },
          { name: 'Reports', href: '/dashboard/reports' }
        ]}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box>
          {contactSites.length > 0 ? (
            <CommonTable
              rows={contactSites}
              columns={columns}
              totalRecords={totalRows}
              pageSize={pageSize}
              loading={loading}
              handlePageChange={(newPage) => setPage(newPage + 1)}
              handlePageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              noAction={true}
            />
          ) : !loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <span>No Data Available</span>
            </div>
          ) : null}
        </Box>
      </Box>
    </Container>
  );
};

export default TransactionReports;
